import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    GetCompanyExistsResponse,
    GetCompanyGapiStatusResponse,
    ProblemDetails,
    RequestTemporaryAccessToDoka360CompanyResponse,
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

/**
 * Requests temporary support agent access to a company in Doka 360 for the currently authenticated user.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useRequestTemporaryAccessToDoka360CompanyHook = () => {
    const requestTemporaryAccessToDoka360Company =
        useCustomWelcomeClient<RequestTemporaryAccessToDoka360CompanyResponse>();

    return useCallback(
        (customerIdentifier: string, signal?: AbortSignal) => {
            return requestTemporaryAccessToDoka360Company({
                url: `/companies/${customerIdentifier}/doka360/temporary-access`,
                method: 'POST',
                signal,
            });
        },
        [requestTemporaryAccessToDoka360Company],
    );
};

export const useRequestTemporaryAccessToDoka360CompanyMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRequestTemporaryAccessToDoka360CompanyHook>>>,
        TError,
        RequestTemporaryAccessToDoka360CompanyMutationVariables,
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRequestTemporaryAccessToDoka360CompanyHook>>>,
    TError,
    RequestTemporaryAccessToDoka360CompanyMutationVariables,
    TContext
> => {
    const mutationKey = ['requestTemporaryAccessToDoka360Company'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const requestTemporaryAccessToDoka360Company = useRequestTemporaryAccessToDoka360CompanyHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useRequestTemporaryAccessToDoka360CompanyHook>>>,
        RequestTemporaryAccessToDoka360CompanyMutationVariables
    > = (props) => {
        const { customerIdentifier } = props ?? {};

        return requestTemporaryAccessToDoka360Company(customerIdentifier);
    };

    return { mutationFn, ...mutationOptions };
};

export type RequestTemporaryAccessToDoka360CompanyMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useRequestTemporaryAccessToDoka360CompanyHook>>>
>;

export type RequestTemporaryAccessToDoka360CompanyMutationError = ErrorType<ProblemDetails>;
export type RequestTemporaryAccessToDoka360CompanyMutationVariables = { customerIdentifier: string };

export const useRequestTemporaryAccessToDoka360Company = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRequestTemporaryAccessToDoka360CompanyHook>>>,
            TError,
            RequestTemporaryAccessToDoka360CompanyMutationVariables,
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useRequestTemporaryAccessToDoka360CompanyHook>>>,
    TError,
    RequestTemporaryAccessToDoka360CompanyMutationVariables,
    TContext
> => {
    return useMutation(useRequestTemporaryAccessToDoka360CompanyMutationOptions(options), queryClient);
};
/**
 * Checks if a company exists<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useCompanyExistsHook = () => {
    const companyExists = useCustomWelcomeClient<GetCompanyExistsResponse>();

    return useCallback(
        (customerIdentifier: string, signal?: AbortSignal) => {
            return companyExists({ url: `/companies/${customerIdentifier}/exists`, method: 'GET', signal });
        },
        [companyExists],
    );
};

export const getCompanyExistsQueryKey = (customerIdentifier: string) => {
    return [`/companies/${customerIdentifier}/exists`] as const;
};

export const useCompanyExistsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getCompanyExistsQueryKey(customerIdentifier);

    const companyExists = useCompanyExistsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>> = ({ signal }) =>
        companyExists(customerIdentifier, signal);

    return {
        queryKey,
        queryFn,
        enabled: customerIdentifier !== null && customerIdentifier !== undefined,
        ...queryOptions,
    } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type CompanyExistsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>>;
export type CompanyExistsQueryError = ErrorType<ProblemDetails>;

export function useCompanyExists<
    TData = Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCompanyExists<
    TData = Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCompanyExists<
    TData = Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useCompanyExists<
    TData = Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useCompanyExistsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useCompanyExistsQueryOptions(customerIdentifier, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Checks if GAPI is enabled for a company<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useGetCompanyGapiStatusHook = () => {
    const getCompanyGapiStatus = useCustomWelcomeClient<GetCompanyGapiStatusResponse>();

    return useCallback(
        (customerIdentifier: string, signal?: AbortSignal) => {
            return getCompanyGapiStatus({ url: `/companies/${customerIdentifier}/gapi-status`, method: 'GET', signal });
        },
        [getCompanyGapiStatus],
    );
};

export const getGetCompanyGapiStatusQueryKey = (customerIdentifier: string) => {
    return [`/companies/${customerIdentifier}/gapi-status`] as const;
};

export const useGetCompanyGapiStatusQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetCompanyGapiStatusQueryKey(customerIdentifier);

    const getCompanyGapiStatus = useGetCompanyGapiStatusHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>> = ({ signal }) =>
        getCompanyGapiStatus(customerIdentifier, signal);

    return {
        queryKey,
        queryFn,
        enabled: customerIdentifier !== null && customerIdentifier !== undefined,
        ...queryOptions,
    } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type GetCompanyGapiStatusQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>
>;
export type GetCompanyGapiStatusQueryError = ErrorType<ProblemDetails>;

export function useGetCompanyGapiStatus<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCompanyGapiStatus<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCompanyGapiStatus<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetCompanyGapiStatus<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    customerIdentifier: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCompanyGapiStatusHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetCompanyGapiStatusQueryOptions(customerIdentifier, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}
